import React, { useLayoutEffect, useRef, useState } from "react";

import Typography from "@mui/material/Typography";

import {
  Answer,
  AnswerButton,
  GeneratedByRead,
  TextFieldWrapper,
} from "@app/analytics/components/KeyQuestionAnswer/styles";

import AutoAnimateContainer from "@rui/AutoAnimateContainer";
import { Column } from "@rui/Stack";
import { Bold } from "@rui/styles";
import TextField from "@rui/TextField";

import type { KeyQuestionAcceptedAnswerProps } from "./types";

const EditableAcceptedAnswer = ({
  answer,
  generatedByRead,
  isEditing = false,
  isSaving = false,
  onClickEdit,
  onSaveAnswer,
}: KeyQuestionAcceptedAnswerProps) => {
  const [answerText, setAnswerText] = useState(answer);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const answerIsSaving = Boolean(answerText) && isSaving;

  useLayoutEffect(() => {
    if (!isEditing || !inputRef.current) return;
    // short timeout to give time for the input to animate in
    setTimeout(() => inputRef.current?.focus());
  }, [isEditing]);

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    setAnswerText(event.target.value);
  };

  const handleSave = () => {
    if (!onSaveAnswer) return;
    onSaveAnswer(answerText);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSave();
    }
  };

  return (
    <>
      <Answer className="editable">
        <Column variant="left" spacing={0.25}>
          <Typography className="key-question-answer__text">
            <Bold>Answer:</Bold> {answer}
          </Typography>
          {generatedByRead && <GeneratedByRead />}
        </Column>
        {!isEditing && (
          <AnswerButton
            className="key-question-answer__edit-answer"
            loading={isSaving}
            onClick={onClickEdit}
          >
            Edit
          </AnswerButton>
        )}
      </Answer>
      <AutoAnimateContainer show={isEditing}>
        <TextFieldWrapper marginRight="55px">
          <TextField
            fullWidth
            disabled={answerIsSaving}
            ref={inputRef}
            value={answerText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <AnswerButton onClick={handleSave} loading={answerIsSaving}>
            Save
          </AnswerButton>
        </TextFieldWrapper>
      </AutoAnimateContainer>
    </>
  );
};

const AcceptedAnswer = (props: KeyQuestionAcceptedAnswerProps) => {
  const canEdit = Boolean(props.onSaveAnswer) && Boolean(props.onClickEdit);

  if (canEdit) {
    return <EditableAcceptedAnswer {...props} />;
  }

  return (
    <Answer>
      <Column variant="left" spacing={0.25}>
        <Typography className="key-question-answer__text">
          <Bold>Answer:</Bold> {props.answer}
        </Typography>
        {props.generatedByRead && <GeneratedByRead />}
      </Column>
    </Answer>
  );
};

export default AcceptedAnswer;
